import { observable, flow, makeObservable } from "mobx";
import { appStore } from "../";
import { EAIClient } from "Services/EAIClient";
import { IDataQuery } from "Stores/commonStore";

export interface EnfluxTerm {
    id: number,
    school_id: string,
    name: string,
    term: string,
    term_type: string,
    calendar_year: string,
    term_order: number,
    acad_year: string,
    start_date: string,
    end_date: string,
    dates_verified: boolean,
    // Active Terms (EAI-5644). Optional because callers that don't manage the
    // Active Terms flag omit it from writes, leaving the API value untouched.
    is_active?: boolean,
}

export interface CanvasConfiguration {
    authorizeUrl: string,
    clientId: string,
    clientKey: string,
    iconUrl: string,
    redirectUriLegacy: string,
    redirectUris: string,
    uiLocked: boolean
}

export class IngestionStore {
    client: EAIClient = new EAIClient();

    @observable isUpdatingTerms = false;
    @observable isUpdatingCourses = false;
    @observable isUpdatingDatasources = false;
    @observable terms: EnfluxTerm[] = [];
    @observable enfluxCourses: IDataQuery | [];
    @observable courseMapping;
    @observable canvasConfiguration: CanvasConfiguration;
    @observable configurableDatasources: Array<object> = [];
    @observable canvasConfigurationExists = false;

    constructor() {
        makeObservable(this);
    }

    @flow
    *fetchTerms() {
        this.isUpdatingTerms = true;
        try {
            const resp = yield this.client.listEnfluxTerms();

            this.terms = resp.data.data.sort((firstTerm, secondTerm) => {
                const fED = new Date(firstTerm.end_date).getTime()
                const sED = new Date(secondTerm.end_date).getTime()
                return fED - sED;
            })

        } catch (e) {
            appStore.addError(e, "Error fetching terms. Please try again.");
        } finally {
            this.isUpdatingTerms = false;
        }
    }

    @flow
    *createTerm(term: Partial<EnfluxTerm>): any {
        this.isUpdatingTerms = true;
        try {
            yield this.client.createEnfluxTerm(term);
            this.fetchTerms();
        } catch (e) {
            this.isUpdatingTerms = false;
            appStore.addError(e, "Error creating term. Please try again.");
        }
    }

    @flow
    *updateTerm(termId: number, term: Partial<EnfluxTerm>): any {
        this.isUpdatingTerms = true;
        try {
            yield this.client.updateEnfluxTerm(termId, term);
            this.fetchTerms();
        } catch (e) {
            this.isUpdatingTerms = false;
            appStore.addError(e, "Error updating term. Please try again.");
        }
    }

    @flow
    *deleteTerm(termId: number): any {
        this.isUpdatingTerms = true;
        try {
            yield this.client.deleteEnfluxTerm(termId);
            this.fetchTerms();
        } catch (e) {
            this.isUpdatingTerms = false;
            appStore.addError(e, "Error deleting term. Please try again.");
        }
    }

    @flow
    *fetchEnfluxCourses(){
        this.isUpdatingCourses = true;
        try {
            const resp = yield this.client.listEnfluxCourses();
            this.enfluxCourses = resp.data.data;
        } catch (e) {
            appStore.addError(e, "Error fetching courses. Please try again.");
        } finally {
            this.isUpdatingCourses = false;
        }
    }

    @flow
    *fetchPaginatedEnfluxCourses(pageSize: number, page: number, order: string, filters: Record<string, any>){
        this.isUpdatingCourses = true;
        try {
            const resp = yield this.client.listPaginatedEnfluxCourses(pageSize, page, order, filters);
            this.enfluxCourses = resp.data.data;
        } catch (e) {
            appStore.addError(e, "Error fetching courses. Please try again.");
        } finally {
            this.isUpdatingCourses = false;
        }
    }

    @flow
    *fetchCourseMapping(enfluxCourseId: number): any {
        try {
            const resp = yield this.client.getCourseMapping(enfluxCourseId);
            this.courseMapping = resp.data.data;
        } catch (e) {
            appStore.addError(e, "Error fetching course mapping. Please try again.");
        }
    }

    @flow
    *createUserOverride(userOverride: any): any {
        try {
            yield this.client.createUserOverride(userOverride);
        } catch (e) {
            appStore.addError(e, "Error creating user override. Please try again.");
        }
    }

    @flow
    *updateUserOverride(userOverrideId: number, userOverride: any): any {
        try {
            yield this.client.updateUserOverride(userOverrideId, userOverride);
        } catch (e) {
            appStore.addError(e, "Error updating user override. Please try again.");
        }
    }

    @flow
    * fetchConfigurableDatasources(): any {
        try {
            const resp = yield this.client.fetchConfigurableDatasources();
            this.configurableDatasources = resp.data.data;
        } catch (e) {
            appStore.addError(e, "Unable to fetch datasources. Please try again.");
        }
    }

    @flow
    * fetchCanvasDatasource(): any {
        try {
            const resp = yield this.client.fetchDatasource("canvas");
            const data = resp.data.data;
            this.canvasConfiguration = {
                authorizeUrl: data.settings.authorize_url,
                clientId: data.settings.client_id,
                clientKey: data.settings.client_secret,
                iconUrl: data.settings.icon_url,
                redirectUriLegacy: data.settings.redirect_uri_legacy,
                redirectUris: data.settings.redirect_uris,
                uiLocked: data.settings.ui_locked === "true"
            };
        } catch (e) {
            appStore.addError(e, "Unable to fetch Canvas Settings. Please try again.");
        }
    }

    @flow
    * checkCanvasDatasourceExists(): any {
        try {
            const resp = yield this.client.checkDatasourceExists("canvas");
            this.canvasConfigurationExists = resp.data.data.exists;
        } catch (e) {
            appStore.addError(e, "Unable to fetch Canvas Settings. Please try again.");
        }
    }

    @flow
    *updateCanvasDatasource(data: any): any {
        try {
            this.isUpdatingDatasources = true;
            yield this.client.updateCanvasDatasource(data);
        } catch (e) {
            if (e.response.data.result['error'] != "InvalidPermissions") {
                appStore.addError(e, "Error updating Canvas client info. Please try again.");
            }
            else { // If its InvalidPermissions, display error message
                appStore.addError(e, e.response.data.result['message']);
            }
            throw e;
        }
        finally {
            this.isUpdatingDatasources = false;
        }
    }
}
